import React, { useReducer, useEffect, useRef } from 'react';
import {
    Box, Button, Typography, Snackbar, Alert
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const DrawingInterface = React.lazy(() => import('../../components/DrawingInterface'));

// Estado inicial
const initialState = {
    selectedEvent: '',
    selectedCategory: [],
    selectedSource: 'inscritos',
    allSelectedSubscribers: new Map(),
    isDrawing: false,
    winner: null,
    winners: [],
    currentDisplay: { name: '', city: '' },
    drawItems: [],
    drawSpeed: 100,
    stateAlert: {
        status: false,
        type: 'success',
        msg: ''
    }
};

// Reducer
function sortedDrawReducer(state, action) {
    switch (action.type) {
        case 'LOAD_CONFIG':
            return {
                ...state,
                selectedEvent: action.payload.selectedEvent,
                selectedCategory: action.payload.selectedCategory,
                selectedSource: action.payload.selectedSource,
                allSelectedSubscribers: action.payload.allSelectedSubscribers
            };

        case 'SET_ALERT':
            return { ...state, stateAlert: action.payload };

        case 'CLOSE_ALERT':
            return { ...state, stateAlert: { ...state.stateAlert, status: false } };

        case 'START_DRAWING':
            return {
                ...state,
                isDrawing: true,
                winner: null,
                drawItems: action.payload
            };

        case 'UPDATE_CURRENT_DISPLAY':
            return { ...state, currentDisplay: action.payload };

        case 'SET_WINNER':
            const newWinner = {
                ...action.payload,
                timestamp: new Date().toLocaleString('pt-BR'),
                drawNumber: state.winners.length + 1
            };
            
            const updatedAllSelected = new Map(state.allSelectedSubscribers);
            const winnerId = action.payload.id || action.payload.subscriberId;
            if (winnerId) {
                updatedAllSelected.delete(winnerId);
            }
            
            return {
                ...state,
                isDrawing: false,
                winner: newWinner,
                winners: [...state.winners, newWinner],
                currentDisplay: action.payload,
                allSelectedSubscribers: updatedAllSelected,
                stateAlert: {
                    status: true,
                    type: 'success',
                    msg: `🎉 ${action.payload.name} foi sorteado(a)!`
                }
            };

        case 'RESET_DRAW':
            const winnersToReinclude = new Map(state.allSelectedSubscribers);
            
            state.winners.forEach(winner => {
                if (winner.id) {
                    const subscriberObj = {
                        id: winner.id,
                        name: winner.name,
                        city: winner.city.split(' - ')[0] || winner.city,
                        state: winner.city.split(' - ')[1] || '',
                        company: winner.company || ''
                    };
                    winnersToReinclude.set(winner.id, subscriberObj);
                }
            });
            
            return {
                ...state,
                isDrawing: false,
                winner: null,
                currentDisplay: { name: '', city: '' },
                drawItems: [],
                allSelectedSubscribers: winnersToReinclude,
                stateAlert: state.winners.length > 0 ? {
                    status: true,
                    type: 'info',
                    msg: `Reset realizado! ${state.winners.length} ganhador(es) foram reincluídos.`
                } : state.stateAlert
            };

        case 'CLEAR_WINNERS':
            return {
                ...state,
                winners: [],
                winner: null,
                currentDisplay: { name: '', city: '' }
            };

        case 'REMOVE_WINNER':
            const updatedWinners = state.winners.filter((_, index) => index !== action.payload);
            const reorderedWinners = updatedWinners.map((winner, index) => ({
                ...winner,
                drawNumber: index + 1
            }));
            return {
                ...state,
                winners: reorderedWinners
            };

        case 'REINCLUDE_WINNER':
            const winnerToReinclude = action.payload;
            const newAllSelectedForReinclude = new Map(state.allSelectedSubscribers);
            
            if (winnerToReinclude.id) {
                newAllSelectedForReinclude.set(winnerToReinclude.id, winnerToReinclude);
            }
            
            return {
                ...state,
                allSelectedSubscribers: newAllSelectedForReinclude,
                stateAlert: {
                    status: true,
                    type: 'info',
                    msg: `${winnerToReinclude.name} foi reincluído(a) nos sorteios!`
                }
            };

        default:
            return state;
    }
}

export default function SortedDraw() {
    const [state, dispatch] = useReducer(sortedDrawReducer, initialState);
    const navigate = useNavigate();
    const drawInterval = useRef();

    // Carregar configuração do localStorage
    useEffect(() => {
        const savedConfig = localStorage.getItem('sortedConfig');
        if (savedConfig) {
            try {
                const config = JSON.parse(savedConfig);
                const subscribersMap = new Map(config.allSelectedSubscribers);
                
                dispatch({
                    type: 'LOAD_CONFIG',
                    payload: {
                        selectedEvent: config.selectedEvent,
                        selectedCategory: config.selectedCategory,
                        selectedSource: config.selectedSource,
                        allSelectedSubscribers: subscribersMap
                    }
                });
            } catch (error) {
                console.error('Erro ao carregar configuração:', error);
                dispatch({
                    type: 'SET_ALERT',
                    payload: {
                        status: true,
                        type: 'error',
                        msg: 'Erro ao carregar configuração. Retorne à tela de configuração.'
                    }
                });
            }
        } else {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Nenhuma configuração encontrada. Redirecionando...'
                }
            });
            setTimeout(() => navigate('/Sorteios'), 2000);
        }
    }, [navigate]);

    // Função de sorteio
    const startDraw = () => {
        if (state.allSelectedSubscribers.size === 0) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Nenhum participante selecionado!'
                }
            });
            return;
        }

        const subscribers = Array.from(state.allSelectedSubscribers.values());
        dispatch({ type: 'START_DRAWING', payload: subscribers });

        let count = 0;
        const totalDuration = 3000;
        const cycles = Math.floor(totalDuration / state.drawSpeed);

        drawInterval.current = setInterval(() => {
            const randomIndex = Math.floor(Math.random() * subscribers.length);
            const selected = subscribers[randomIndex];

            dispatch({
                type: 'UPDATE_CURRENT_DISPLAY',
                payload: {
                    name: selected.name,
                    city: [selected.city, selected.state].filter(Boolean).join(' - '),
                    company: selected.company
                }
            });

            count++;
            if (count >= cycles) {
                clearInterval(drawInterval.current);
                // tenta escolher um ganhador não repetido
                const existingWinners = state.winners || [];
                const pickUniqueWinner = (items, existingWinners, maxAttempts = 10) => {
                    const existingIds = new Set(existingWinners.map(w => String(w.id || w.subscriberId || '')));
                    const existingNames = new Set(existingWinners.map(w => String(w.name || '').toLowerCase()));
                    for (let attempt = 0; attempt < maxAttempts; attempt++) {
                        const candidate = items[Math.floor(Math.random() * items.length)];
                        const candId = String(candidate.id || candidate.subscriberId || '');
                        const candName = String(candidate.name || '').toLowerCase();
                        if (candId && existingIds.has(candId)) continue;
                        if (candName && existingNames.has(candName)) continue;
                        return candidate;
                    }
                    return null;
                };

                const winner = pickUniqueWinner(subscribers, existingWinners, 20);
                if (!winner) {
                    dispatch({ type: 'SET_ALERT', payload: { status: true, type: 'warning', msg: 'Não foi possível encontrar um ganhador não repetido. Reinclua ganhadores ou aumente a seleção.' } });
                    dispatch({ type: 'SET_WINNER', payload: { id: null, name: '(Sem ganhador)', city: '', company: '' } });
                    return;
                }

                dispatch({
                    type: 'SET_WINNER',
                    payload: {
                        id: winner.id,
                        subscriberId: winner.subscriberId,
                        name: winner.name,
                        city: [winner.city, winner.state].filter(Boolean).join(' - '),
                        company: winner.company,
                        email: winner.email || '',
                        category: winner.category || ''
                    }
                });
            }
        }, state.drawSpeed);
    };

    const resetDraw = () => {
        if (drawInterval.current) {
            clearInterval(drawInterval.current);
        }
        dispatch({ type: 'RESET_DRAW' });
    };

    const exportWinners = () => {
        if (state.winners.length === 0) {
            dispatch({
                type: 'SET_ALERT',
                payload: {
                    status: true,
                    type: 'warning',
                    msg: 'Nenhum ganhador para exportar'
                }
            });
            return;
        }

        const exportData = state.winners.map(winner => ({
            'Nº Sorteio': winner.drawNumber,
            'Nome': winner.name,
            'Cidade/Estado': winner.city,
            'Empresa': winner.company,
            'Email': winner.email,
            'Categoria': winner.category,
            'Data/Hora': winner.timestamp
        }));

        const ws = XLSX.utils.json_to_sheet(exportData);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Ganhadores');

        const date = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
        XLSX.writeFile(wb, `ganhadores_sorteio_${state.selectedEvent || 'arquivo'}_${date}.xlsx`);

        dispatch({
            type: 'SET_ALERT',
            payload: {
                status: true,
                type: 'success',
                msg: 'Lista de ganhadores exportada com sucesso!'
            }
        });
    };

    const handleBackToConfig = () => {
        navigate('/Sorteios');
    };

    return (
        <Box sx={{ width: '100%', p: 3 }}>
            <Snackbar
                open={state.stateAlert.status}
                autoHideDuration={6000}
                onClose={() => dispatch({ type: 'CLOSE_ALERT' })}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity={state.stateAlert.type} onClose={() => dispatch({ type: 'CLOSE_ALERT' })}>
                    {state.stateAlert.msg}
                </Alert>
            </Snackbar>

            <Box sx={{ mb: 2 }}>
                <Button variant="outlined" onClick={handleBackToConfig}>
                    ← Voltar para Configuração
                </Button>
            </Box>

            {state.selectedEvent || state.allSelectedSubscribers.size > 0 ? (
                <React.Suspense fallback={<Typography>Carregando...</Typography>}>
                    <DrawingInterface
                        selectedEvent={state.selectedEvent}
                        selectedSource={state.selectedSource}
                        selectedCategory={state.selectedCategory}
                        allSelectedSubscribers={state.allSelectedSubscribers}
                        isDrawing={state.isDrawing}
                        winner={state.winner}
                        winners={state.winners}
                        currentDisplay={state.currentDisplay}
                        onStartDraw={startDraw}
                        onResetDraw={resetDraw}
                        onExportWinners={exportWinners}
                        onClearWinners={() => dispatch({ type: 'CLEAR_WINNERS' })}
                        onRemoveWinner={(index) => dispatch({ type: 'REMOVE_WINNER', payload: index })}
                        onReincludeWinner={(winner) => {
                            const subscriberToReinclude = {
                                id: winner.id,
                                name: winner.name,
                                city: winner.city.split(' - ')[0],
                                state: winner.city.split(' - ')[1],
                                company: winner.company
                            };
                            dispatch({ type: 'REINCLUDE_WINNER', payload: subscriberToReinclude });
                        }}
                    />
                </React.Suspense>
            ) : (
                <Typography variant="h6" align="center" sx={{ mt: 5 }}>
                    Carregando configuração do sorteio...
                </Typography>
            )}
        </Box>
    );
}
